import PropTypes from "prop-types"
import React, { useCallback, useEffect, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation } from "react-query"

import Form from "src/components/Form"

import { createLongtermWaitlistEntry } from "src/api/Waitlist/longTerm/entries"

import { useTracker } from "src/hooks/use_tracker"

import BoaterForm from "./BoaterForm"
import SignInSignOutToggle from "./SignInSignOutToggle"

const SERVER_FAILURE_MESSAGE =
  "Something went wrong and you weren't added to the waitlist. Please try again."

const BoaterLongtermWaitlistFormPage = ({
  marina,
  currentUser: initialUser,
  boats: initialBoats,
  tracking,
  initialValues = {},
}) => {
  // After popup auth, SignInSignOutToggle hydrates these from /account/me +
  // /account/boats so the form can flip into its logged-in branch
  // (BoatSelectorSection instead of ContactSection) without a page reload.
  const [currentUser, setCurrentUser] = useState(initialUser)
  const [boats, setBoats] = useState(initialBoats || [])

  const methods = useForm({ defaultValues: initialValues })
  const { clearErrors } = methods

  // Errors set on the contact fields outlive the unmounted Contact section and
  // would keep the submit button disabled.
  const handleAuthSuccess = useCallback(
    (user, userBoats) => {
      setCurrentUser(user)
      setBoats(userBoats || [])
      clearErrors("contact")
    },
    [clearErrors]
  )
  const tracker = useTracker()

  useEffect(() => {
    tracker.trackEvent(tracking.view, tracking.eventProperties)
  }, [tracker, tracking])

  const { mutate, isLoading, isSuccess } = useMutation(
    ["longtermWaitlist", marina.slug],
    (data) => createLongtermWaitlistEntry(marina.slug, data),
    {
      onError: ({ message, status }) => {
        methods.setError("root.error", {
          message: status >= 500 ? SERVER_FAILURE_MESSAGE : message,
        })
      },
      onSuccess: (response) => {
        window.location.assign(new URL(response.redirectTo))
      },
    }
  )

  const onSubmit = (values) => {
    tracker.trackEvent(tracking.submit, tracking.eventProperties)
    const formValues = { ...values }
    if (formValues.boat_id === "addNew") {
      delete formValues.boat_id
    }
    // Fields from the signed-out branch keep their values after an in-form sign-in.
    if (currentUser) {
      delete formValues.contact
    }
    mutate(formValues)
  }

  return (
    <div>
      <h1>Join the Waitlist at {marina.name}</h1>
      <h4>Complete the form below to join the long-term storage waitlist</h4>
      <hr />
      <div className="flex flex-col gap-8">
        <SignInSignOutToggle
          currentUser={currentUser}
          onAuthSuccess={handleAuthSuccess}
        />
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <BoaterForm
              marinaName={marina.name}
              marinaId={marina.id}
              boats={boats}
              isLoggedIn={Boolean(currentUser)}
              registrationRequired={marina.registrationRequirements !== "none"}
              insuranceRequired={marina.insuranceRequirements !== "none"}
              isLoading={isLoading}
              isSuccess={isSuccess}
            />
          </Form>
        </FormProvider>
      </div>
    </div>
  )
}

BoaterLongtermWaitlistFormPage.propTypes = {
  currentUser: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }),
  marina: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    registrationRequirements: PropTypes.string,
    insuranceRequirements: PropTypes.string,
  }).isRequired,
  boats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired
  ),
  initialValues: PropTypes.object,
  tracking: PropTypes.shape({
    eventProperties: PropTypes.shape({
      marina_id: PropTypes.string.isRequired,
      marina_name: PropTypes.string.isRequired,
    }).isRequired,
    view: PropTypes.string.isRequired,
    submit: PropTypes.string.isRequired,
  }).isRequired,
}

export default BoaterLongtermWaitlistFormPage
